import { z } from "zod";
import { examCodeSchema, programmeCodeSchema } from "../programmes";

/**
 * Seed-data interfaces (spec §13.4c). Phase 4 fills them; Phase 0 fixes their shape so every phase
 * builds against the same contracts. Seed data never mixes with real users and is removable by teardown.
 */

export const REFERENCE_KINDS = ["lab_values", "dosage_rules", "prioritisation_frameworks", "disallowed_content"] as const;
export const referenceKindSchema = z.enum(REFERENCE_KINDS);

export const labValueSchema = z.object({
  analyte: z.string().min(1),
  specimen: z.string().min(1),
  low: z.number().nullable(),
  high: z.number().nullable(),
  unit: z.string().min(1),
  note: z.string().optional(),
});

export const dosageRuleSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  formula: z.string().min(1),
  variables: z.record(z.string(), z.string()),
  rounding: z.object({ unit: z.string(), decimals: z.number().int().min(0).max(4), mode: z.enum(["half_up", "down", "up"]) }),
});

export const frameworkSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  /** Programme codes the framework applies to. Plain strings, so versions published before plan BE18 still read. */
  programmes: z.array(z.string().min(1)).min(1),
});

export const disallowedRuleSchema = z.object({
  key: z.string().min(1),
  description: z.string().min(1),
});

export const referenceDataSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("lab_values"), version: z.string(), items: z.array(labValueSchema).min(1) }),
  z.object({ kind: z.literal("dosage_rules"), version: z.string(), items: z.array(dosageRuleSchema).min(1) }),
  z.object({ kind: z.literal("prioritisation_frameworks"), version: z.string(), items: z.array(frameworkSchema).min(1) }),
  z.object({ kind: z.literal("disallowed_content"), version: z.string(), items: z.array(disallowedRuleSchema).min(1) }),
]);
export type ReferenceData = z.infer<typeof referenceDataSchema>;

export const demoAccountSchema = z.object({
  email: z.string().email().endsWith("@demo.studyward.app"),
  display_name: z.string().min(1),
  role: z.enum(["student", "owner", "moderator", "support", "finance"]),
  programme: programmeCodeSchema.nullable(),
  exam: examCodeSchema.nullable(),
  exam_date: z.string().nullable(),
  timezone: z.string().min(1),
  plan: z.enum(["free", "pro"]),
});
export type DemoAccount = z.infer<typeof demoAccountSchema>;

export const exampleSetSchema = z.object({
  programme: programmeCodeSchema,
  exam: examCodeSchema,
  title: z.string().min(1),
  source: z.object({ title: z.string(), licence: z.literal("CC BY 4.0"), url: z.string().url(), attribution: z.string() }),
  owner_email: z.string().email(),
});
export type ExampleSet = z.infer<typeof exampleSetSchema>;

/** Recorded AI response for one pipeline stage — CI never calls a live model (spec §12.3). */
export const pipelineFixtureSchema = z.object({
  programme: programmeCodeSchema,
  stage: z.enum(["ingest", "chunk", "generate", "verify"]),
  case: z.enum(["pass", "regenerate", "quarantine"]),
  request: z.unknown(),
  response: z.unknown(),
});
export type PipelineFixture = z.infer<typeof pipelineFixtureSchema>;
